import _ from 'lodash'
import dayjs from 'dayjs'

/*
Gets the details for all the rounds for a medication.
*/
export const getRoundDetails = ({
  emarRounds,
  emarRoundTiming,
  resident,
  residentMed,
  carers,
}) => {
  return _(emarRounds)
    .filter({ emarRoundTiming, resident })
    .map(({ residentMeds, ...round }) => {
      const details = _.find(residentMeds, { residentMed })
      if (!details || !details.log || details.log.deleted) return null

      return {
        ...round,
        date: dayjs(round.timeLogged).format('DD-MM-YYYY'),
        taken: details.taken,
        reasonCode: details.reasonCode,
        note: details.note,
        log: {
          ...details.log,
          loggedBy: _.find(carers, { id: round?.loggedBy }),
        },
      }
    })
    .filter((d) => d) // filter out null
    .value()
}

/*
Gets all PRN rounds for the medication.

Example output: [
    { date: 01-01-2021, emarRoundTiming: 'test1', log: { ... }, ... },
    { date: 01-01-2021, emarRoundTiming: 'test2', log: { ... }, ... },
    { date: 02-01-2021, emarRoundTiming: 'test3', log: { ... }, ... },
    { date: 03-01-2021, emarRoundTiming: 'test3', log: { ... }, ... },
]
*/
export const getPRNRounds = ({
  medication,
  emarRounds,
  resident,
  carers,
}) => {
  const roundDetails = []
  emarRounds?.filter(round => round.resident === resident)
    .forEach(({ residentMeds, ...round }) => {
      const details = _.find(residentMeds, { residentMed: medication.id })
      if (!details || !details.log || details.log.deleted) return null

      roundDetails.push({
        ...round,
        date: dayjs(round.timeLogged).format('DD-MM-YYYY'),
        taken: details.taken,
        reasonCode: details.reasonCode,
        note: details.note,
        log: {
          ...details.log,
          loggedBy: _.find(carers, { id: round?.loggedBy }),
        },
      })

      return null
    })

  return roundDetails
      // Gets an array of all PRN rounds for this medication. Different rounds on the same
      // day are separate entries in the array
}

/*
Formats the PRN rounds.

Groups the rounds by date. prnRounds contains all the round logs for that day.
Each log corresponds to a completed round.

Example output: [
    { date: 01-01-2021, taken: true, type: 'PRN', prnRounds: [ {...}, {...} ] },
    { date: 02-01-2021, taken: true, type: 'PRN', prnRounds: [ {...} ] },
    { date: 03-01-2021, taken: true, type: 'PRN', prnRounds: [ {...} ] },
]

*/

export const formatPRNRounds = (PRNRounds) => {
  return _(PRNRounds)
    .groupBy((round) => round.date)
    .mapValues((rounds) => {
      return {
        type: 'PRN',
        taken: true,
        prnRounds: _.sortBy(
          rounds,
          'timeLogged'
        ),
        date: rounds[0]?.date,
      }
    })
    .values()
    .value()
}

/*

Gets the PRN Round Timings collection. Because all PRN rounds are displayed
on the same row, there is only one entry for this collection.

Example output: [{
    time: 'PRN',
    dose: 1,
    altDose: 2,
    rounds: see example output for formatPRNRounds
}]

*/

export const getPRNRoundTimings = ({
  medication,
  resident,
  emarRounds,
  carers,
}) => {
  const PRNRounds = getPRNRounds({
    medication,
    resident,
    emarRounds,
    carers,
  })
  const formattedPRNRounds = formatPRNRounds(PRNRounds)

  return [
    {
      time: 'PRN',
      dose: medication.dose,
      altDose: medication.altDose,
      rounds: formattedPRNRounds,
    },
  ]
}

// Formats PRN med for MAR chart
export const getPRNMedication = ({
  medication,
  resident,
  emarRounds,
  carers,
}) => {
  return {
    ...medication,
    startDate: dayjs(medication.startDate).format('DD-MM-YYYY'),
    endDate: medication.endDate
      ? dayjs(medication.endDate).format('DD-MM-YYYY')
      : null,
    roundTimings: getPRNRoundTimings({
      medication,
      resident,
      emarRounds,
      carers,
    }),
  }
}

// Formats non-PRN med for MAR chart
export const getNonPRNMedication = ({
  carers,
  emarRoundTimings,
  emarRounds,
  medication,
  resident,
}) => {
  return {
    ...medication,
    startDate: dayjs(medication.startDate).format('DD-MM-YYYY'),
    endDate: medication.endDate
      ? dayjs(medication.endDate).format('DD-MM-YYYY')
      : null,
    roundTimings: medication.emarRoundsDetails
      .map((roundDetails) => {
        /*
            roundDetails = { dose, altDose, emarRoundTiming }
            */
        const timing = _.find(emarRoundTimings, {
          id: roundDetails.emarRoundTiming,
        })

        if (!timing) return null

        return {
          time: timing?.time || '',
          emarRoundTiming: timing.id,
          dose: roundDetails.dose,
          altDose: roundDetails.altDose,
          rounds: getRoundDetails({
            emarRounds,
            emarRoundTiming: timing.id,
            resident,
            residentMed: medication.id,
            carers,
          }),
        }
      })
      .filter((d) => d),
  }
}

/**
@description Sorts residents depending on their home sort setting.
Either sorts alphabetically or by room number.

...

@param {bool} residentSortByRoomNumber. True if residents
are sorted by room number. False if sorted alphabetically
by preferred/first name.

@returns {function} sortResidentByHomeSetting
*/

export const sortResidentByHomeSetting = (props) => {
  const { resident, residentSortByRoomNumber } = props

  return residentSortByRoomNumber
    ? resident.location?.room
    : resident?.preferredName
    ? resident?.preferredName
    : resident.name.first
}

/**
@description Gets details for residents in round. Also
sorts them by signed/unsigned, then by default sort.

...

@returns {array} Returns populated collection of residents,
sorted first by signed/unsigned, and then by default sort.
*/

export const getRoundResidents = ({
  roundResidents,
  carers,
  residentsDetails,
  residentSortByRoomNumber,
  logs,
}) => {
  return _(roundResidents)
    .map((residentRound) => ({
      ...residentRound,
      loggedBy: _.find(carers, { id: residentRound?.loggedBy }),
      witnessedBy: _.find(carers, { id: residentRound.witnessedBy }),
      resident: _.find(residentsDetails, { id: residentRound.resident?.id ?? residentRound.resident }),
      signed: !!residentRound?.loggedBy,
      meds: _(residentRound.meds)
        .orderBy('PRN', 'desc')
        .map((med) => {
          return {
            ...med,
            log: _.find(logs, { id: med.logId }),
          }
        })
        .value(),
    }))
    .filter({ resident: { status: 'active' } })
    .sortBy('signed', (round) =>
      sortResidentByHomeSetting({
        resident: round.resident,
        residentSortByRoomNumber,
      })
    )
    .value()
}
